import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Loader2, TrendingUp, DollarSign, Filter, Share2 } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n || 0);

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/reports`);
      setData(r.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    if (error) {
      return <div data-testid="reports-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
    }
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading reports…
      </div>
    );
  }

  const { totals, funnel, revenueByService, bySource, byUtmSource } = data;
  const first = funnel[0]?.reached || 0;
  const winRate = totals.leads ? Math.round((100 * totals.won) / totals.leads) : 0;

  return (
    <div data-testid="admin-reports" className="space-y-8">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Reports</h1>
        <p className="mt-2 text-mid-neutral">
          Pipeline conversion, revenue by service and lead source performance.
        </p>
      </header>

      {/* Headline stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total leads" value={totals.leads} Icon={TrendingUp} testId="report-total-leads" />
        <Stat label="Won" value={totals.won} Icon={TrendingUp} testId="report-won" />
        <Stat label="Win rate" value={`${winRate}%`} Icon={BarChart3} testId="report-win-rate" />
        <Stat label="Active MRR" value={fmtMoney(totals.activeMRR)} Icon={DollarSign} testId="report-mrr" accent />
      </div>

      {/* Funnel */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <Filter className="h-4 w-4 text-mint" /> Pipeline funnel
        </h2>
        <div className="mt-5 space-y-3">
          {funnel.map((s) => (
            <div key={s.stage} data-testid={`funnel-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-forest">{s.stage}</span>
                <span className="text-mid-neutral">
                  {s.reached} lead{s.reached === 1 ? "" : "s"}
                  {s.conversionFromPrev != null && ` · ${s.conversionFromPrev}% from previous`}
                </span>
              </div>
              <div className="mt-1 h-3 rounded-full bg-soft overflow-hidden">
                <div
                  className="h-full rounded-full bg-forest transition-[width]"
                  style={{ width: first ? `${Math.max(4, (100 * s.reached) / first)}%` : "4%" }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-mid-neutral">
          Funnel shows leads that reached each stage (current position). Lost leads ({totals.lost}) are excluded.
        </p>
      </section>

      {/* Revenue by service */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <DollarSign className="h-4 w-4 text-mint" /> Monthly revenue by service
        </h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral">
                <th className="pb-2">Service</th>
                <th className="pb-2">Active</th>
                <th className="pb-2">Paused</th>
                <th className="pb-2">Completed</th>
                <th className="pb-2 text-right">Active MRR</th>
              </tr>
            </thead>
            <tbody>
              {revenueByService.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-mid-neutral">No services assigned to clients yet.</td></tr>
              )}
              {revenueByService.map((r) => (
                <tr key={r.serviceName} data-testid={`revenue-${r.serviceName.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-t border-border-neutral">
                  <td className="py-2.5 font-bold text-forest">{r.serviceName}</td>
                  <td className="py-2.5 text-forest">{r.activeCount}</td>
                  <td className="py-2.5 text-forest">{r.pausedCount}</td>
                  <td className="py-2.5 text-forest">{r.completedCount}</td>
                  <td className="py-2.5 text-right font-bold text-forest">{fmtMoney(r.activeMRR)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Source performance */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <Share2 className="h-4 w-4 text-mint" /> Lead source performance
        </h2>
        <div className="mt-4 grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-2">By channel</p>
            <table className="w-full text-sm">
              <tbody>
                {bySource.map((s) => (
                  <tr key={s.source} data-testid={`source-${s.source}`} className="border-t border-border-neutral">
                    <td className="py-2.5 font-semibold text-forest">
                      {s.source === "growth-assessment" ? "Growth Assessment" : "Discovery Booking"}
                    </td>
                    <td className="py-2.5 text-forest">{s.total} leads</td>
                    <td className="py-2.5 text-forest">{s.won} won</td>
                    <td className="py-2.5 text-right font-bold text-forest">{s.wonRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-2">By UTM source</p>
            <table className="w-full text-sm">
              <tbody>
                {byUtmSource.map((s) => (
                  <tr key={s.utmSource} data-testid={`utm-${s.utmSource.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className="border-t border-border-neutral">
                    <td className="py-2.5 font-semibold text-forest">{s.utmSource}</td>
                    <td className="py-2.5 text-forest">{s.total} leads</td>
                    <td className="py-2.5 text-forest">{s.won} won</td>
                    <td className="py-2.5 text-right font-bold text-forest">{s.wonRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, Icon, accent = false, testId }) {
  return (
    <div data-testid={testId}
      className={"rounded-2xl p-5 " + (accent ? "bg-forest text-white" : "bg-white border border-border-neutral text-forest")}>
      <div className="flex items-center justify-between">
        <p className={"text-xs font-bold uppercase tracking-[0.15em] " + (accent ? "text-lime" : "text-mid-neutral")}>{label}</p>
        <Icon className={"h-4 w-4 " + (accent ? "text-lime" : "text-mint")} />
      </div>
      <p className="mt-3 text-3xl font-extrabold">{value}</p>
    </div>
  );
}
